import { useCallback, useEffect, useRef, useState } from 'react'

import { useLastData } from '@/hooks/useLastData'
import { useLocalState } from '@/hooks/useLocalState'
import * as Sentry from '@sentry/nextjs'

import type { ApolloError } from '@apollo/client'

import { useAppSelector } from '@/redux/hooks'
import { selectUserId } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'

import {
  STALE_SNOOZE_DURATION_MS,
  STALE_SNOOZE_STORAGE_KEY_PREFIX,
  STALE_THRESHOLD_HOURS,
} from '../constants/constants'
import { getStaleCutoff } from '../utils/utils'

/**
 * Poll cadence matches the staleness threshold — incidents can only cross the
 * threshold slowly, so a tighter cadence buys nothing. Each tick recomputes
 * the cutoff variable (which is what triggers the refetch; a fixed Apollo
 * pollInterval would re-send an ever-staler timestamp). The tick is a data
 * refresh only; it never un-dismisses the banner (ENG-32181), since snooze
 * expiry solely owns reappearance.
 */
export const STALE_POLL_INTERVAL_MS = STALE_THRESHOLD_HOURS * 60 * 60 * 1000

/** Both count queries select exactly this shape. */
type StaleCountData = {
  outstandingIncidents?: { totalCount?: number } | null
}

interface UseStaleBannerStateIProps<VariablesT> {
  analytics: { bannerDismiss: string; bannerView: string }
  buildVariables: (cutoff: string) => VariablesT
  isEnabled: boolean
  /**
   * Copy prerequisites resolved (the banner interpolates the Org's name,
   * ENG-32179). Folded into visibility here so the view event fires in step
   * with the paint the operator actually sees, not before it.
   */
  isReady: boolean
  /** Sentry `action` tag for count-query failures. */
  sentryAction: string
  /** The variant's generated count-query hook; called unconditionally. */
  useCountQuery: (options: { skip: boolean; variables: VariablesT }) => {
    data?: StaleCountData
    error?: ApolloError
    refetch: () => Promise<unknown>
  }
}

export interface UseStaleBannerStateResultIProps {
  staleCount: number
  isBannerVisible: boolean
  dismiss: () => void
  refetchCount: () => void
}

/**
 * Shared banner state for both stale work variants (operator-scoped and
 * team-wide): the hourly cutoff refresh, the count held through refetch
 * blips, visibility, the rolling 24-hour snooze (ENG-32181), the off-cycle
 * count refresh, and the banner's view/dismiss analytics (owned here so
 * events survive component remounts). Each variant supplies only its query
 * hook, variables, gate, and analytics.
 *
 * The snooze deadline persists in localStorage keyed per user id, so it
 * survives refreshes and doesn't leak across operators on a shared
 * workstation. useLocalState reads its key once at mount, so StaleWork gates
 * mounting on the user id being resolved: the key must be stable for the
 * lifetime of this hook.
 */
export const useStaleBannerState = <VariablesT>({
  analytics,
  buildVariables,
  isEnabled,
  isReady,
  sentryAction,
  useCountQuery,
}: UseStaleBannerStateIProps<VariablesT>): UseStaleBannerStateResultIProps => {
  const userId = useAppSelector(selectUserId)
  // Enforce the per-user-key invariant here, not just at the StaleWork mount
  // gate: with no resolved user id the storage key would degrade to
  // ":undefined" and be shared by every user on the machine, so the banner
  // (and with it the only path that persists a snooze deadline) stays off.
  const isUserResolved = Boolean(userId)
  const isBannerEnabled = isEnabled && isUserResolved
  const [cutoff, setCutoff] = useState(getStaleCutoff)
  const [snoozeUntil, setSnoozeUntil] = useLocalState<number>(
    `${STALE_SNOOZE_STORAGE_KEY_PREFIX}:${userId}`,
    0
  )

  useEffect(() => {
    if (!isBannerEnabled) return undefined
    const interval = setInterval(() => {
      setCutoff(getStaleCutoff())
    }, STALE_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isBannerEnabled])

  // Re-render when the snooze deadline passes so the banner reappears while
  // the tab stays open; a deadline already in the past needs no timer (the
  // visibility check below is already true), which is what makes a refresh
  // after expiry render the banner immediately.
  const [, setSnoozeExpiryTick] = useState(0)
  useEffect(() => {
    const remainingMs = snoozeUntil - Date.now()
    if (remainingMs <= 0) return undefined
    const timeout = setTimeout(
      () => setSnoozeExpiryTick((tick) => tick + 1),
      remainingMs
    )
    return () => clearTimeout(timeout)
  }, [snoozeUntil])

  const { data, error, refetch } = useCountQuery({
    skip: !isBannerEnabled,
    variables: buildVariables(cutoff),
  })

  useEffect(() => {
    if (error) {
      // Banner is non-critical: render nothing on error, but tell Sentry.
      Sentry.captureException(error, {
        tags: { action: sentryAction, feature: 'stale_work' },
      })
    }
  }, [error, sentryAction])

  // Hold the last result through the hourly tick's refetch: the variable
  // change makes Apollo report data: undefined while loading, and without
  // this the banner would blink out and re-fire its view event every hour.
  const lastData = useLastData(data)
  const staleCount = lastData?.outstandingIncidents?.totalCount ?? 0

  const isBannerVisible =
    isBannerEnabled &&
    isReady &&
    !error &&
    staleCount > 0 &&
    Date.now() >= snoozeUntil

  // Fire the view event once per appearance, re-armed when the banner hides.
  const hasTrackedViewRef = useRef(false)
  useEffect(() => {
    if (!isBannerVisible) {
      hasTrackedViewRef.current = false
      return
    }
    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true
      trackEvent(analytics.bannerView, { stale_count: staleCount })
    }
  }, [analytics.bannerView, isBannerVisible, staleCount])

  const dismiss = useCallback(() => {
    trackEvent(analytics.bannerDismiss, { stale_count: staleCount })
    setSnoozeUntil(Date.now() + STALE_SNOOZE_DURATION_MS)
  }, [analytics.bannerDismiss, setSnoozeUntil, staleCount])

  // Off-cycle count refresh, used when the modal's list settles at zero so
  // the banner hides (or corrects its count) immediately instead of waiting
  // for the hourly tick.
  const refetchCount = useCallback(() => {
    // A rejection surfaces through the hook's `error`; see the Sentry effect.
    refetch().catch(() => undefined)
  }, [refetch])

  return {
    dismiss,
    isBannerVisible,
    refetchCount,
    staleCount,
  }
}
